/**
 * useBrowserBus — subscribe the React app to the BrowserBus SSE stream
 * and invalidate tanstack-query caches when relevant data_changed
 * events arrive.
 *
 * The end-effect: an MCP tool call (deploy_widget, update_workspace,
 * archive_account, etc.) on ANY connected client (in-page chat AND
 * external Claude / ChatGPT / Cursor) propagates to the React app
 * instantly. Widgets appear / lists refresh / counts update without
 * the user having to refresh. (There is no in-app chat; the user's own
 * AI client over MCP is the only AI driver.)
 *
 * Server side: tools that mutate state call broadcastDataChanged()
 * with { entity_type, entity_id, workspace_id }. This hook maps each
 * entity_type to the tanstack-query keys to invalidate. Adding a new
 * entity_type that matters to React = add one line to ENTITY_TO_KEYS.
 *
 * Connection lifecycle: one EventSource per React app mount, kept open
 * for the session. SSE auto-reconnects on transient drops.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { dispatchPlanProposed, dispatchPlanStepUpdate } from "./plan-events.js";
import { recordRecentChange } from "./use-recently-changed.js";
import {
  LIVE_AI_AGENT_ACTIVITY_EVENT,
  LIVE_AI_AGENT_SESSION_EVENT,
  LIVE_AI_CONNECTION_EVENT,
  LIVE_AI_DRIVING_EVENT,
  LIVE_AI_TOOL_ACTIVITY_EVENT,
  isAgentFollowMode,
  isBrowserDrivePaused,
  type LiveAgentActivity,
  type LiveAgentSession,
  type LiveToolActivity,
} from "./live-ai-session-events.js";
import {
  ingestAgentActivity,
  ingestAgentSession,
  ingestLegacyToolActivity,
  noteLiveAgentEvent,
  replayAgentActivity,
} from "./agent-activity-store.js";

export interface DataChangedEvent {
  entity_type: string;
  entity_id?: string;
  workspace_id?: string;
  reason?: string;
  /** Durable outbox cursor. Absent on legacy actor-only broadcasts. */
  event_id?: string;
}

interface ChoreographyEvent {
  tool: string;
  input: Record<string, unknown>;
  mediator?: string;
  is_active_window?: boolean;
}

// Built-in choreography handlers — DOM-level operations that work on
// any page without per-page wiring. set_row_status sets data attributes
// on matching elements (CSS rules render the badge); flash adds a
// transient class. Pages that need richer behavior register custom
// handlers on window.__pageContext.<onToolName>.
const CHORE_FLASH_DURATION_MS = 1400;

function dispatchChoreography(ev: ChoreographyEvent) {
  // ── Plan / checklist events — surface to TaskChecklist panel ─────
  // These come from external MCP clients (Claude Desktop / Cursor)
  // driving plans via the MCP tool surface.
  if (ev.tool === "propose_plan") {
    try { dispatchPlanProposed(ev.input as any); } catch (e) { console.warn("[choreography] plan proposed failed:", e); }
    return;
  }
  if (ev.tool === "update_plan_step") {
    try { dispatchPlanStepUpdate(ev.input as any); } catch (e) { console.warn("[choreography] plan step failed:", e); }
    return;
  }

  // ── Background task tray → unified operations store ──────────────
  // start/update/complete_background_task are server-registered
  // choreography tools. The durable ledger may not know them (they're
  // visual-only), so synthesize store events keyed on task_id — one
  // ephemeral session per task.
  if (ev.tool === "start_background_task"
    || ev.tool === "update_background_task"
    || ev.tool === "complete_background_task") {
    const input = ev.input as any;
    const taskId = String(input?.task_id ?? "");
    if (taskId) {
      const terminal = ev.tool === "complete_background_task";
      const status = terminal ? (input?.status ?? "success") : "running";
      ingestAgentActivity({
        event_id: `bg:${taskId}:${ev.tool}:${Date.now()}`,
        session_id: `bg-task:${taskId}`,
        session_seq: 0,
        kind: terminal
          ? (status === "failed" ? "session_failed" : "session_completed")
          : ev.tool === "start_background_task" ? "session_started" : "tool_progress",
        phase: terminal ? status : "running",
        source: "external_mcp",
        mediator: ev.mediator ?? "other_mcp",
        title: ev.tool === "start_background_task"
          ? String(input?.name ?? "Background task")
          : String(input?.message ?? input?.result_summary ?? "Background task update"),
        summary: terminal ? input?.result_summary : input?.message,
        progress_current: typeof input?.progress === "number" ? input.progress : undefined,
        progress_total: typeof input?.total_steps === "number" ? input.total_steps : undefined,
        artifact_url: typeof input?.action_url === "string" ? input.action_url : undefined,
        emitted_at: new Date().toISOString(),
      });
      window.dispatchEvent(new CustomEvent("cmcp:background-task", {
        detail: { tool: ev.tool, input },
      }));
    }
    return;
  }

  // First check window.__pageContext for a custom handler — the page
  // might want richer-than-DOM behavior (e.g. animateGraph on the
  // network analyzer). Fall through to built-in DOM handlers if none.
  const pageCtx = (window as any).__pageContext as Record<string, any> | undefined;
  if (pageCtx) {
    // Two naming conventions are both supported:
    //  - on-prefix PascalCase: 'set_row_status' → 'onSetRowStatus' (preferred)
    //  - bare camelCase: 'set_node_color' → 'setNodeColor' (legacy, used by
    //    NetworkAnalyzerPage)
    const parts = ev.tool.split("_");
    const onPrefixed = "on" + parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("");
    const bareCamel = parts[0] + parts.slice(1).map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("");
    for (const name of [onPrefixed, bareCamel]) {
      const fn = pageCtx[name];
      if (typeof fn === "function") {
        try { fn(ev.input); return; } catch (e) { console.warn("[choreography] handler", name, "failed:", e); }
      }
    }
  }

  if (ev.tool === "set_row_status") {
    const sel = String((ev.input as any).selector ?? "");
    const status = String((ev.input as any).status ?? "");
    const label = String((ev.input as any).label ?? "");
    if (!sel || !status) return;
    document.querySelectorAll(sel).forEach((el) => {
      (el as HTMLElement).setAttribute("data-row-status", status);
      if (label) (el as HTMLElement).setAttribute("data-row-label", label);
      else (el as HTMLElement).removeAttribute("data-row-label");
    });
    return;
  }

  if (ev.tool === "flash") {
    const sel = String((ev.input as any).selector ?? "");
    const color = String((ev.input as any).color ?? "success");
    const duration = Number((ev.input as any).duration_ms ?? CHORE_FLASH_DURATION_MS);
    if (!sel) return;
    document.querySelectorAll(sel).forEach((el) => {
      (el as HTMLElement).classList.add(`chore-flash-${color}`);
      setTimeout(() => {
        (el as HTMLElement).classList.remove(`chore-flash-${color}`);
      }, duration);
    });
    return;
  }

  if (ev.tool === "clear_visual_state") {
    const scope = String((ev.input as any).scope ?? "all");
    if (scope === "all" || scope === "row_status") {
      document.querySelectorAll("[data-row-status]").forEach((el) => {
        (el as HTMLElement).removeAttribute("data-row-status");
        (el as HTMLElement).removeAttribute("data-row-label");
      });
    }
    if (scope === "all" || scope === "flash") {
      document.querySelectorAll("[class*='chore-flash-']").forEach((el) => {
        const cls = (el as HTMLElement).className.split(/\s+/).filter((c) => !c.startsWith("chore-flash-"));
        (el as HTMLElement).className = cls.join(" ");
      });
    }
    if (scope === "all" || scope === "client_context") {
      window.dispatchEvent(new CustomEvent("cmcp:client-context", { detail: null }));
    }
    return;
  }

  // ── Built-in app-wide handlers ─────────────────────────────────────
  // These are the choreography tools the AI fires that should ALWAYS
  // produce a visible effect, regardless of what page the user is on.
  // Without these the user reports "nothing happens in the app" — a
  // regression from the framework's promise that BrowserBus drives the
  // application, not just the chat panel. (FB-MOW4PA89, FB-MOW550ZT.)

  if (ev.tool === "navigate_to") {
    const url = String((ev.input as any)?.url ?? "");
    if (!url) return;
    let target: URL;
    try { target = new URL(url, location.origin); } catch { return; }
    const targetHost = target.hostname.replace(/^www\./i, "");
    const currentHost = location.hostname.replace(/^www\./i, "");
    if (target.protocol !== location.protocol || targetHost !== currentHost) {
      console.warn("[choreography] blocked cross-origin navigation", target.href);
      return;
    }
    // Preserve workspace_id query param when not already present in the
    // target URL — every CongressMCP route reads it.
    const wsId = new URLSearchParams(location.search).get("workspace_id");
    if (wsId && !target.searchParams.has("workspace_id")) {
      target.searchParams.set("workspace_id", wsId);
    }
    location.href = `${target.pathname}${target.search}${target.hash}`;
    return;
  }

  if (ev.tool === "open_intel_page") {
    const t = String((ev.input as any)?.entity_type ?? "").toLowerCase();
    const id = String((ev.input as any)?.id ?? "");
    const view = String((ev.input as any)?.view ?? "").toLowerCase();
    let url: string | null = null;
    // Type-aware route resolution, so external MCP clients see one
    // consistent contract.
    if (t === "member" && id) {
      url = `/members/${encodeURIComponent(id)}`;
      if (view === "network") url += "/network";
    } else if (t === "bill" && id) {
      url = `/bills/${encodeURIComponent(id)}`;
      if (view === "whip-count" || view === "whip_count") url += "/whip-count";
    } else if (t === "contact" && id) url = `/contacts/${encodeURIComponent(id)}`;
    else if (t === "account" && id) url = `/accounts/${encodeURIComponent(id)}`;
    else if (t === "coalition" && id) url = `/coalition-dashboard?workspace_id=${encodeURIComponent(id)}`;
    else if (t === "issue" && id) url = `/issues/${encodeURIComponent(id)}/whip-count`;
    if (!url) return;
    // Routes inside the React app live under /app/*. Auto-prefix when
    // the AI passes a raw path, but preserve absolute paths the user
    // might have constructed manually.
    if (!url.startsWith("/app/") && !url.startsWith("http")) {
      url = "/app" + url;
    }
    const wsId = new URLSearchParams(location.search).get("workspace_id");
    if (wsId && !url.includes("workspace_id=")) {
      url += (url.includes("?") ? "&" : "?") + "workspace_id=" + encodeURIComponent(wsId);
    }
    location.href = url;
    return;
  }

  if (ev.tool === "highlight_entities") {
    // Glow + scroll-into-view rows matching any of the given IDs. Pages
    // tag rows with data-bioguide-id / data-bill-ref / data-contact-id /
    // data-organization-id by convention; this handler reads all four.
    const input = ev.input as any;
    const buckets: Array<[string, string[]]> = [
      ["data-bioguide-id", input?.bioguide_ids ?? []],
      ["data-bill-ref", input?.bill_refs ?? []],
      ["data-contact-id", input?.contact_ids ?? []],
      ["data-organization-id", input?.organization_ids ?? []],
    ];
    let firstHit: HTMLElement | null = null;
    for (const [attr, ids] of buckets) {
      for (const id of ids as string[]) {
        const sel = `[${attr}="${cssEscape(id)}"]`;
        document.querySelectorAll(sel).forEach((el) => {
          (el as HTMLElement).classList.add("chore-flash-info");
          (el as HTMLElement).setAttribute("data-row-status", "info");
          if (!firstHit) firstHit = el as HTMLElement;
          setTimeout(() => {
            (el as HTMLElement).classList.remove("chore-flash-info");
          }, 1800);
        });
      }
    }
    if (firstHit) {
      try { (firstHit as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    }
    return;
  }

  if (ev.tool === "set_client_context") {
    // Context banner pinned at the top of every page. We dispatch a
    // window CustomEvent that the <ClientContextBanner> component (in
    // Layout) listens for. Pass null to clear.
    const payload = ev.input as any;
    window.dispatchEvent(
      new CustomEvent("cmcp:client-context", {
        detail: payload?.text
          ? { text: String(payload.text), kind: payload.kind ?? "info" }
          : null,
      })
    );
    return;
  }

  if (ev.tool === "apply_page_filter") {
    // No DOM-level fallback — page-specific filter shapes vary too much
    // (party/state/chamber on the members list, status/priority on
    // attention items, etc.). Pages that support filtering register an
    // applyPageFilter / onApplyPageFilter handler on window.__pageContext.
    // If we got here without a page handler, dispatch a generic event the
    // page can still listen for as a last resort.
    window.dispatchEvent(
      new CustomEvent("cmcp:apply-page-filter", { detail: ev.input })
    );
    return;
  }

  if (ev.tool === "render_dashboard_panel") {
    // The dashboard feed is the home base for AI-rendered panels; they
    // stack chronologically. <DashboardFeed>
    // (mounted on /app/dashboard) listens for cmcp:dashboard-feed-panel
    // and accumulates panels there. If the user isn't currently on the
    // dashboard, the panel still gets persisted to localStorage —
    // they'll see it next time they open the dashboard.
    window.dispatchEvent(
      new CustomEvent("cmcp:dashboard-feed-panel", { detail: ev.input })
    );
    return;
  }

  if (ev.tool === "clear_dashboard_feed") {
    window.dispatchEvent(new CustomEvent("cmcp:dashboard-feed-clear"));
    return;
  }

  // Push-notification subscribe flow. The AI fires this when the user
  // says "let me know when..." — usePushNotifications listens for the
  // CustomEvent below and walks the browser's permission flow.
  if (ev.tool === "request_push_subscription") {
    window.dispatchEvent(
      new CustomEvent("cmcp:request-push-subscription", { detail: ev.input }),
    );
    return;
  }

  // Force-refresh screenshot. The page hook listens, captures via
  // html2canvas, uploads to /api/v1/workspaces/:wsId/page-context with
  // the screenshot field set. AI calls this when it wants ground-truth
  // visual confirmation mid-turn (after a navigate_to / compose_panel /
  // any choreography that should produce visible change).
  if (ev.tool === "take_screenshot") {
    window.dispatchEvent(
      new CustomEvent("cmcp:take-screenshot", { detail: ev.input }),
    );
    return;
  }

  // Catch-all fallback — any choreography tool we didn't explicitly
  // dispatch above lands here. Emit a generic CustomEvent that pages
  // can listen for opportunistically + log to console so debugging
  // a "nothing happened" report doesn't require source-diving the
  // dispatcher. Without this, an unknown tool name (e.g. a new tool
  // shipped server-side before the client deploys) silently no-ops.
  //
  // Pages opt in by listening for "cmcp:choreography" with
  //   { detail: { tool, input } }
  // Useful for graph-heavy pages that handle a long tail of paint_*
  // and set_node_* tools — they can branch on tool name in one
  // handler instead of registering N onSetNodeX functions.
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent("cmcp:choreography", {
          detail: { tool: ev.tool, input: ev.input },
        }),
      );
    } catch { /* swallow */ }
  }
   
  console.warn(
    `[choreography] no handler matched for tool "${ev.tool}". Check window.__pageContext.on${
      ev.tool.split("_").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("")
    } registration on the current page, or listen for the cmcp:choreography window event.`,
  );
}

/**
 * Ambient watch: when an agent event names an entity that is rendered on
 * the current page (pages tag rows with data-bill-ref / data-bioguide-id /
 * data-contact-id / data-organization-id), pulse it briefly. This is the
 * default — unlike explicit choreography it never scrolls or navigates.
 */
const ENTITY_MARKER_ATTRS: Record<string, string> = {
  bill: "data-bill-ref",
  member: "data-bioguide-id",
  contact: "data-contact-id",
  account: "data-organization-id",
  organization: "data-organization-id",
};

function pulseEntityDomMarker(entityType: string, entityId: string): void {
  const attr = ENTITY_MARKER_ATTRS[entityType];
  if (!attr) return;
  const sel = `[${attr}="${cssEscape(entityId)}"]`;
  document.querySelectorAll(sel).forEach((el) => {
    (el as HTMLElement).classList.add("chore-flash-info");
    setTimeout(() => (el as HTMLElement).classList.remove("chore-flash-info"), 1800);
  });
}

function pulseAgentEntityMarker(activity: LiveAgentActivity): void {
  if (isBrowserDrivePaused()) return;
  if (!activity.entity_type || !activity.entity_id) return;
  if (activity.kind !== "data_mutation" && activity.kind !== "entity_focus"
    && activity.kind !== "tool_succeeded") return;
  pulseEntityDomMarker(activity.entity_type, activity.entity_id);
}

/**
 * Follow mode (opt-in, per browser): artifact_published and entity_focus
 * events may navigate this window to the produced artifact. Ambient watch
 * remains the default so long backend operations never yank the page.
 */
function followAgentIfEnabled(activity: LiveAgentActivity): void {
  if (!isAgentFollowMode() || isBrowserDrivePaused()) return;
  if (activity.kind !== "artifact_published" && activity.kind !== "entity_focus") return;
  const url = activity.artifact_url;
  if (!url || !url.startsWith("/")) return;
  let target: URL;
  try { target = new URL(url, location.origin); } catch { return; }
  if (target.origin !== location.origin) return;
  const wsId = new URLSearchParams(location.search).get("workspace_id");
  if (wsId && !target.searchParams.has("workspace_id")) {
    target.searchParams.set("workspace_id", wsId);
  }
  location.href = `${target.pathname}${target.search}${target.hash}`;
}

// CSS.escape polyfill — older targets and some non-Chromium browsers
// don't have it. Used by highlight_entities's [attr="..."] selector.
function cssEscape(s: string): string {
  if (typeof (window as any).CSS?.escape === "function") {
    return (window as any).CSS.escape(s);
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, "_");
}

/**
 * Map a `data_changed` event to the tanstack-query keys that should be
 * invalidated. Host apps inject their own — the framework doesn't ship
 * an entity-type vocabulary.
 */
// Key segments may be undefined when an event lacks a workspace/entity id;
// tanstack treats the key as a prefix match either way.
export type KeysToInvalidate = (ev: DataChangedEvent) => Array<Array<string | undefined>>;

export interface UseBrowserBusOptions {
  /**
   * Map a data_changed event to tanstack-query keys. Returning [] means
   * "no React surfaces care about this entity_type yet."
   */
  keysToInvalidate?: KeysToInvalidate;
  /**
   * SSE endpoint path. Defaults to /api/browser-stream — override only if
   * the host app mounts the bus on a different route.
   */
  streamPath?: string;
  /**
   * sessionStorage key for the tab id. Override to namespace if multiple
   * BrowserBus instances coexist on the same page.
   */
  tabIdStorageKey?: string;
  /**
   * The currently selected workspace. Passing it explicitly makes the
   * connection follow React workspace changes instead of relying on a
   * query parameter captured during the first mount.
   */
  workspaceId?: string | null;
  /** Current React route, used when registering the open browser window. */
  pagePath?: string;
}

function defaultTabId(storageKey: string): string {
  let t = sessionStorage.getItem(storageKey);
  if (!t) {
    t = "rt_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem(storageKey, t);
  }
  return t;
}

export function useBrowserBus(options: UseBrowserBusOptions = {}) {
  const qc = useQueryClient();
  const keysToInvalidate = options.keysToInvalidate;
  const streamPath = options.streamPath ?? "/api/browser-stream";
  const tabIdStorageKey = options.tabIdStorageKey ?? "cmcp:react-tab-id";
  const workspaceId = options.workspaceId !== undefined
    ? options.workspaceId
    : new URLSearchParams(location.search).get("workspace_id");
  const pagePath = options.pagePath ?? location.pathname;

  useEffect(() => {
    const cursorStorageKey = workspaceId
      ? `cmcp:workspace-event-cursor:${workspaceId}`
      : null;
    const params = new URLSearchParams({
      tab_id: defaultTabId(tabIdStorageKey),
      page_path: pagePath,
    });
    if (workspaceId) params.set("workspace_id", workspaceId);
    const url = `${streamPath}?${params.toString()}`;
    let es: EventSource | null = null;
    let disposed = false;
    let replaying = false;
    let replayTimer: ReturnType<typeof setTimeout> | null = null;
    const replayAbort = new AbortController();
    window.dispatchEvent(new CustomEvent(LIVE_AI_CONNECTION_EVENT, { detail: { state: "connecting" } }));
    try {
      es = new EventSource(url, { withCredentials: true });
    } catch (err) {
       
      console.warn("[useBrowserBus] EventSource unavailable:", err);
      return;
    }

    const advanceCursor = (eventId?: string) => {
      if (!cursorStorageKey || !eventId || !/^\d+$/.test(eventId)) return;
      const current = sessionStorage.getItem(cursorStorageKey) ?? "0";
      if (!/^\d+$/.test(current) || BigInt(eventId) > BigInt(current)) {
        sessionStorage.setItem(cursorStorageKey, eventId);
      }
    };

    const applyDataChanged = (ev: DataChangedEvent) => {
      try {
        if (disposed) return;
        // Actor broadcasts reach every tab, including tabs viewing another
        // workspace. They may invalidate that workspace's cached queries,
        // but must not light up the current page or advance its replay cursor.
        const visibleHere = !ev.workspace_id || ev.workspace_id === workspaceId;
        // Re-dispatch as a window event with the parsed payload as
        // detail so anyone listening (cell-flash, status pill, etc.)
        // gets the entity_type / entity_id without parsing themselves.
        if (visibleHere) window.dispatchEvent(
          new CustomEvent("cmcp:browser-bus-data-changed", { detail: ev })
        );
        // Ambient change marker — data_changed applies to every producer
        // (external MCP, chat, playbook, REST), so on-screen rows pulse
        // even when no agent_activity event carries the entity ref.
        if (ev.entity_type && ev.entity_id && !isBrowserDrivePaused()) {
          pulseEntityDomMarker(ev.entity_type, ev.entity_id);
        }
        // Record into the recent-changes registry for useRecentlyChanged.
        // Lazy-imported to avoid forcing every host app to take the
        // hook as a hard dep.
        try {
           
          if (visibleHere) recordRecentChange(ev);
        } catch { /* swallow */ }
        if (!keysToInvalidate) return; // host didn't wire up — pure SSE listener
        const keys = keysToInvalidate(ev);
        for (const k of keys) {
          qc.invalidateQueries({ queryKey: k });
        }
        if (keys.length > 0) {
           
          console.log(`[useBrowserBus] ${ev.entity_type} → invalidated ${keys.length} queries`, ev.reason);
        }
      } catch (err) {
         
        console.warn("[useBrowserBus] data_changed apply failed", err);
      }
    };

    const replayWorkspaceChanges = async () => {
      if (disposed || replaying || replayTimer !== null || !workspaceId || !cursorStorageKey) return;
      replaying = true;
      let continueReplay = false;
      try {
        const savedCursor = sessionStorage.getItem(cursorStorageKey) ?? "0";
        let after = /^\d+$/.test(savedCursor) ? savedCursor : "0";
        // Limit each batch to 1,000 events, then yield to rendering and user
        // input before continuing. A long-lived tab must finish its backlog
        // without depending on another connection or dropping unseen events.
        for (let page = 0; page < 5; page++) {
          if (disposed || replayAbort.signal.aborted) return;
          const response = await fetch(
            `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/changes?after=${encodeURIComponent(after)}&limit=200`,
            { credentials: "include", signal: replayAbort.signal },
          );
          if (disposed) return;
          if (!response.ok) throw new Error(`replay failed (${response.status})`);
          const body = await response.json() as {
            events?: DataChangedEvent[];
            next_cursor?: string;
            has_more?: boolean;
          };
          if (disposed) return;
          for (const ev of body.events ?? []) {
            if (ev.workspace_id === workspaceId) applyDataChanged(ev);
          }
          // Only a fully processed replay page proves we consumed every
          // event through this cursor. A newer live event cannot prove
          // that, and saving its id could skip missed changes on reconnect.
          const nextCursor = body.next_cursor;
          const progressed = !!nextCursor && /^\d+$/.test(nextCursor)
            && BigInt(nextCursor) > BigInt(after);
          if (progressed) {
            advanceCursor(nextCursor);
            after = nextCursor;
          }
          if (!body.has_more) return;
          // A malformed/non-advancing page must not create an endless timer
          // loop. Keep the last completed cursor for the next reconnect.
          if (!progressed || (body.events ?? []).length === 0) {
            throw new Error("replay reported more events without advancing its cursor");
          }
        }
        continueReplay = true;
      } catch (err) {
        // Live delivery still works; the next reconnect retries failures.
        if (!disposed) console.warn("[useBrowserBus] durable replay failed", err);
      } finally {
        replaying = false;
        if (continueReplay && !disposed && !replayAbort.signal.aborted) {
          replayTimer = setTimeout(() => {
            replayTimer = null;
            void replayWorkspaceChanges();
          }, 25);
        }
      }
    };

    es.addEventListener("data_changed", (e: MessageEvent) => {
      try {
        applyDataChanged(JSON.parse(e.data) as DataChangedEvent);
      } catch (err) {
         
        console.warn("[useBrowserBus] data_changed parse failed", err);
      }
    });

    // Choreography events — set_row_status / flash / clear_visual_state
    // / page-specific handlers. Built-in DOM dispatch handles the
    // generic ones; pages register richer custom handlers on
    // window.__pageContext.onSetRowStatus etc.
    es.addEventListener("choreography", (e: MessageEvent) => {
      try {
        const ev = JSON.parse(e.data) as ChoreographyEvent;
        if (ev.is_active_window && ev.mediator && ev.mediator !== "web_ui") {
          window.dispatchEvent(new CustomEvent(LIVE_AI_DRIVING_EVENT, {
            detail: { state: "active", mediator: ev.mediator },
          }));
        }
        if (isBrowserDrivePaused()) return;
        window.dispatchEvent(new CustomEvent("cmcp:browser-bus-choreography"));
        dispatchChoreography(ev);
      } catch (err) {
         
        console.warn("[useBrowserBus] choreography parse failed", err);
      }
    });

    es.addEventListener("tool_activity", (e: MessageEvent) => {
      try {
        const activity = JSON.parse(e.data) as LiveToolActivity;
        window.dispatchEvent(new CustomEvent(LIVE_AI_TOOL_ACTIVITY_EVENT, { detail: activity }));
        // Bridge into the unified operations store so pre-ledger producers
        // still render in the grouped Live Operations surfaces.
        ingestLegacyToolActivity(activity);
      } catch (err) {
         
        console.warn("[useBrowserBus] tool_activity parse failed", err);
      }
    });

    // ── Live Operations stream ─────────────────────────────────────────
    // agent_session / agent_activity are the durable-ledger events. They
    // feed the shared store (rail + /app/live) and re-dispatch as window
    // events for ad-hoc listeners.
    es.addEventListener("agent_session", (e: MessageEvent) => {
      try {
        const session = JSON.parse(e.data) as LiveAgentSession;
        ingestAgentSession(session);
        window.dispatchEvent(new CustomEvent(LIVE_AI_AGENT_SESSION_EVENT, { detail: session }));
      } catch (err) {
        console.warn("[useBrowserBus] agent_session parse failed", err);
      }
    });

    es.addEventListener("agent_activity", (e: MessageEvent) => {
      try {
        const activity = JSON.parse(e.data) as LiveAgentActivity;
        ingestAgentActivity(activity);
        noteLiveAgentEvent(activity.workspace_id, activity.event_id);
        window.dispatchEvent(new CustomEvent(LIVE_AI_AGENT_ACTIVITY_EVENT, { detail: activity }));
        pulseAgentEntityMarker(activity);
        followAgentIfEnabled(activity);
      } catch (err) {
        console.warn("[useBrowserBus] agent_activity parse failed", err);
      }
    });

    es.addEventListener("claim_window", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as { mediator?: string; ttl_seconds?: number };
        window.dispatchEvent(new CustomEvent(LIVE_AI_DRIVING_EVENT, {
          detail: { state: "active", mediator: data.mediator ?? "external", ttl_seconds: data.ttl_seconds },
        }));
      } catch {}
    });

    es.addEventListener("mediator_active", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as { mediator?: string };
        window.dispatchEvent(new CustomEvent(LIVE_AI_DRIVING_EVENT, {
          detail: { state: "other_tab", mediator: data.mediator ?? "external" },
        }));
      } catch {}
    });

    es.addEventListener("release_window", (e: MessageEvent) => {
      let mediator: string | undefined;
      try { mediator = (JSON.parse(e.data) as { mediator?: string }).mediator; } catch {}
      window.dispatchEvent(new CustomEvent(LIVE_AI_DRIVING_EVENT, {
        detail: { state: "released", mediator },
      }));
    });

    es.addEventListener("ping", () => {
      // Keepalive — also marks the SSE connection alive for the
      // status pill. Without periodic pings the pill goes stale even
      // when the connection is fine.
      window.dispatchEvent(new CustomEvent("cmcp:browser-bus-ping"));
      window.dispatchEvent(new CustomEvent(LIVE_AI_CONNECTION_EVENT, { detail: { state: "live" } }));
    });

    es.addEventListener("hello", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
         
        console.log("[useBrowserBus] connected:", data.connection_id);
        window.dispatchEvent(
          new CustomEvent("cmcp:browser-bus-hello", { detail: data })
        );
        window.dispatchEvent(new CustomEvent(LIVE_AI_CONNECTION_EVENT, { detail: { state: "live" } }));

        // SSE delivery is intentionally best-effort. On every successful
        // connect/reconnect, replay durable workspace changes after the last
        // cursor this tab observed so deploys and network gaps cannot leave
        // React Query surfaces stale.
        void replayWorkspaceChanges();

        // Same reconnect contract for the agent-operations ledger: replay
        // durable agent_activity rows after this tab's saved cursor so the
        // Live Operations rail and /app/live survive deploys and drops.
        void replayAgentActivity(workspaceId ?? null).catch((err) => {
          console.warn("[useBrowserBus] agent activity replay failed", err);
        });
      } catch {}
    });

    es.onerror = () => {
      // EventSource auto-reconnects on transient drops. Surface the
      // disconnect to the status pill so the user can see if their
      // connection is dead — addresses FB-MOW4PACV (silent stale
      // tab registrations).
      window.dispatchEvent(new CustomEvent("cmcp:browser-bus-dropped"));
      window.dispatchEvent(new CustomEvent(LIVE_AI_CONNECTION_EVENT, { detail: { state: "dropped" } }));
    };

    return () => {
      disposed = true;
      if (replayTimer !== null) clearTimeout(replayTimer);
      replayTimer = null;
      replayAbort.abort();
      try { es?.close(); } catch {}
    };
  }, [qc, keysToInvalidate, streamPath, tabIdStorageKey, workspaceId, pagePath]);
}
